import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AdministracionService } from 'src/data/services/admin/administracion.service';
import { firstValueFrom } from 'rxjs';
import { CanalData, Subcanal, EquipoConUsuarios, NodoArbol, EquipoAgrupado, Usuario, Coordinador, Equipo } from '../channel-hierarchy.model';
import { LoggerService } from 'src/app/core/logger.service';

@Component({
    selector: 'app-channel-hierarchy',
    templateUrl: './channel-hierarchy.component.html',
    styleUrls: ['./channel-hierarchy.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelHierarchyComponent implements OnInit {
  public canalData: CanalData | null = null;
  public equiposCanal: EquipoConUsuarios[] = [];
  public subcanalData: Subcanal[] = [];
  public equiposSubcanal: EquipoConUsuarios[] = [];
  public loadingE = false;
  public equiposAgrupadosSubcanal: { [idSubcanal: number]: EquipoAgrupado } = {};

  // ESTRUCTURA DEL ÁRBOL
  public equiposArbol: NodoArbol[] = [];

  constructor(
    private location: Location,
    private router: Router,
    private administracionService: AdministracionService,
    private logger: LoggerService
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.canalData = navigation?.extras.state?.canalData ?? null;
    this.subcanalData = navigation?.extras.state?.channelSubchannel ?? [];

    this.logger.debug('Datos recibidos Canal data:', this.canalData);
    this.logger.debug('Datos recibidos Subcanal:', this.subcanalData);
  }

  ngOnInit(): void {
    if (this.canalData?.id) {
      // this.obtenerEquipos(this.canalData.id, false, this.canalData.name);
      this.getTeamsByChannel(
        this.canalData.id,
        false,
        this.canalData.name
      );
    }

    if (this.subcanalData.length > 0) {
      this.subcanalData.forEach((subcanal: Subcanal) => {
        // this.obtenerEquipos(subcanal.id, true, subcanal.name)
        this.getTeamsByChannel(subcanal.id, true, subcanal.name)
          .then(() => {
            // SI EL SUBCANAL NO TIENE EQUIPOS AGREGO UN ARRAY VACIO PARA LUEGO PODER  MOSTRAR EL NOMBRE
            if (!this.equiposAgrupadosSubcanal[subcanal.id]) {
              this.equiposAgrupadosSubcanal[subcanal.id] = { nombre: subcanal.name, equipos: [] };
            }
            this.crearEstructuraArbol();
          });
      });
    }
  }

  async getTeamsByChannel(idCanal: number, subcanal: boolean, nombreCanal: string): Promise<void> {
    this.loadingE = true;
    try {
      const resp: { data: Equipo[] } = await firstValueFrom(
        subcanal ? this.administracionService.getTeamBySubChannel(idCanal) : this.administracionService.getTeamsByChannel(idCanal)
      );

      const equipos = resp?.data ?? [];
      if (!equipos.length) return;

      for (const equipo of equipos) {
        let [usuarios, coordinador] = await Promise.all([
          this.obtenerUsuariosPorEquipo(equipo.id, equipo.name),
          this.obtenerCoordinadorPorEquipo(equipo.id, equipo.name)
        ]);

        /**
         * Evitamos mostrar como coordinador
         * a una persona que ya aparece como usuario.
         */
        const usuariosIds = new Set(usuarios.map((usuario: Usuario) => usuario.id));
        coordinador = coordinador.filter((c: Coordinador) => !usuariosIds.has(c.id));

        const equipoConUsuarios: EquipoConUsuarios = {
          equipoId: equipo.id,
          nombreEquipo: equipo.name,
          usuarios,
          coordinadorEquipo: coordinador,
          ...(subcanal ? { idSubcanal: idCanal, nombreSubcanal: nombreCanal } : {})
        };

        if (subcanal) {
          if (!this.equiposAgrupadosSubcanal[idCanal]) {
            this.equiposAgrupadosSubcanal[idCanal] = { nombre: nombreCanal, equipos: [] };
          }
          this.equiposAgrupadosSubcanal[idCanal].equipos.push(equipoConUsuarios);
        } else {
          this.equiposCanal.push(equipoConUsuarios);
        }
      }

      this.crearEstructuraArbol();

    } catch (error) {
      console.error('Error al llamar al servicio:', error);
    } finally {
      this.loadingE = false;
    }
    this.logger.debug("Equipos finales Canal:", this.equiposCanal);
    this.logger.debug("Equipos finales agrupados Subcanal:", this.equiposAgrupadosSubcanal);
  }

  crearEstructuraArbol(): void {
    this.equiposArbol = [];

    /**
     * =========================
     * CANAL
     * =========================
     */
    if (this.equiposCanal.length > 0) {
      const canalNodo: NodoArbol = {
        label: 'CANAL: ' + this.canalData?.name,
        data: undefined,
        icon: 'fas fa-building',
        children: this.equiposCanal.map(equipo => ({
          label: 'EQUIPO: ' + equipo.nombreEquipo,
          data: equipo,
          icon: 'fas fa-users',
          children: [
            /**
             * COORDINADOR
             */
            {
              label: 'COORDINADOR: ' + (equipo.coordinadorEquipo.length > 0 ? equipo.coordinadorEquipo[0].username : 'No disponible'),
              data: equipo.coordinadorEquipo[0] || {},
              icon: 'fas fa-user-tie',
            },
            ...equipo.usuarios.map(usuario => ({
              label: 'USUARIO: ' + usuario.username,
              data: usuario,
              icon: 'fas fa-user'
            }))
          ]
        }))
      };
      this.equiposArbol.push(canalNodo);
    }

    // AGREGAR SUBCANALES AL ÁRBOL
    Object.values(this.equiposAgrupadosSubcanal).forEach((subcanal: EquipoAgrupado) => {
      const subcanalNodo: NodoArbol = {
        label: 'SUBCANAL: ' + subcanal.nombre,
        icon: 'fas fa-building',
        data: subcanal,
        children: subcanal.equipos.length > 0 ? subcanal.equipos.map(equipo => ({
          label: 'EQUIPO: ' + equipo.nombreEquipo,
          data: equipo,
          icon: 'fas fa-users',
          children: [
            /**
             * COORDINADOR
             */
            {
              label: 'COORDINADOR: ' + (equipo.coordinadorEquipo.length > 0 ? equipo.coordinadorEquipo[0].username : 'No disponible'),
              icon: 'fas fa-user-tie',
              data: equipo.coordinadorEquipo[0] || {}
            },
            /**
             * USUARIOS
             */
            ...equipo.usuarios.map(usuario => ({
              label: 'USUARIO: ' + usuario.username,
              data: usuario,
              icon: 'fas fa-user',
            }))
          ]
        })) : [{
          label: 'No hay equipos en este subcanal',
          icon: 'fas fa-exclamation-circle',
          data: {}
        }]
      };

      this.equiposArbol.push(subcanalNodo);
    });
  }

  async obtenerUsuariosPorEquipo(idEquipo: number, nombreEquipo: string): Promise<Usuario[]> {
    try {
      const resp: { data: Usuario[] } = await firstValueFrom(this.administracionService.getUsersByTeam(idEquipo));
      if (resp && resp.data) {
        const usuarios = resp.data;
        this.logger.debug(`Usuarios del equipo ${nombreEquipo}:`, usuarios);
        return usuarios;
      } else {
        return [];
      }
    } catch (error) {
      console.error(`Error al obtener los usuarios del equipo ${nombreEquipo}:`, error);
      return [];
    }
  }

  async obtenerCoordinadorPorEquipo(idEquipo: number, nombreEquipo: string): Promise<Coordinador[]> {
    try {
      const res: { data: Coordinador[] } = await firstValueFrom(this.administracionService.getCoordinatorByTeam(idEquipo));
      const coordinador = res?.data ?? [];
      this.logger.debug(`Coordinador del equipo ${nombreEquipo}:`, coordinador);
      return coordinador;
    } catch (error) {
      console.error(`Error obteniendo el coordinador para el equipo ${idEquipo}`, error);
      return [];
    }
  }

  goBack(): void {
    this.location.back();
  }
}
